import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  fetchPrestations,
  createPrestation,
  updatePrestation,
  deletePrestation,
} from '../../store/prestationSlice';
import type { Prestation } from '../../types';

const Prestations: React.FC = () => {
  const dispatch = useAppDispatch();
  const { prestations, loading, error } = useAppSelector((state) => state.prestation);
  
  const [open, setOpen] = useState(false);
  const [editingPrestation, setEditingPrestation] = useState<Prestation | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    name_en: '',
    description: '',
    type: 'OPTION' as 'OPTION' | 'ASSURANCE',
    pricing_mode: 'flat' as 'flat' | 'daily',
    tax_rate: 20,
    allows_quantity: false,
    min_quantity: 1,
    max_quantity: 1,
    track_stock: false,
    stock_quantity: null as number | null,
    is_active: true,
    sort_order: 0,
  });

  useEffect(() => {
    dispatch(fetchPrestations());
  }, [dispatch]);

  const handleOpen = (prestation?: Prestation) => {
    if (prestation) {
      setEditingPrestation(prestation);
      setFormData({
        code: prestation.code,
        name: prestation.name,
        name_en: prestation.name_en || '',
        description: prestation.description || '',
        type: prestation.type,
        pricing_mode: prestation.pricing_mode,
        tax_rate: prestation.tax_rate,
        allows_quantity: prestation.allows_quantity,
        min_quantity: prestation.min_quantity,
        max_quantity: prestation.max_quantity,
        track_stock: prestation.track_stock ?? false,
        stock_quantity: prestation.stock_quantity ?? null,
        is_active: prestation.is_active,
        sort_order: prestation.sort_order,
      });
    } else {
      setEditingPrestation(null);
      setFormData({
        code: '',
        name: '',
        name_en: '',
        description: '',
        type: 'OPTION',
        pricing_mode: 'flat',
        tax_rate: 20,
        allows_quantity: false,
        min_quantity: 1,
        max_quantity: 1,
        track_stock: false,
        stock_quantity: null,
        is_active: true,
        sort_order: 0,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingPrestation(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingPrestation) {
        await dispatch(updatePrestation({ id: editingPrestation.id, data: formData })).unwrap();
      } else {
        await dispatch(createPrestation(formData)).unwrap();
      }
      handleClose();
      dispatch(fetchPrestations());
    } catch (err) {
      console.error('Error saving prestation:', err);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette prestation ?')) {
      try {
        await dispatch(deletePrestation(id)).unwrap();
        dispatch(fetchPrestations());
      } catch (err) {
        console.error('Error deleting prestation:', err);
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Prestations</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Nouvelle Prestation
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Mode de tarification</TableCell>
              <TableCell align="center">TVA</TableCell>
              <TableCell align="center">Stock</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : prestations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Aucune prestation trouvée
                </TableCell>
              </TableRow>
            ) : (
              prestations.map((prestation) => (
                <TableRow key={prestation.id}>
                  <TableCell>{prestation.code}</TableCell>
                  <TableCell>{prestation.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={prestation.type}
                      color={prestation.type === 'ASSURANCE' ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{prestation.pricing_mode === 'flat' ? 'Forfait' : 'Journalier'}</TableCell>
                  <TableCell align="center">{Number(prestation.tax_rate).toFixed(0)} %</TableCell>
                  <TableCell align="center">
                    {prestation.track_stock ? (
                      Number(prestation.stock_quantity ?? 0) <= 0 ? (
                        <Chip label="Rupture" size="small" color="error" />
                      ) : (
                        <Typography variant="body2" component="span">{prestation.stock_quantity}</Typography>
                      )
                    ) : (
                      <Typography variant="body2" color="text.secondary" component="span">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={prestation.is_active ? 'Actif' : 'Inactif'}
                      color={prestation.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpen(prestation)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(prestation.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingPrestation ? 'Modifier la Prestation' : 'Nouvelle Prestation'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Nom"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Nom (EN)"
              value={formData.name_en}
              onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
              fullWidth
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as 'OPTION' | 'ASSURANCE' })}
                label="Type"
              >
                <MenuItem value="OPTION">Option</MenuItem>
                <MenuItem value="ASSURANCE">Assurance</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Mode de tarification</InputLabel>
              <Select
                value={formData.pricing_mode}
                onChange={(e) => setFormData({ ...formData, pricing_mode: e.target.value as 'flat' | 'daily' })}
                label="Mode de tarification"
              >
                <MenuItem value="flat">Forfait</MenuItem>
                <MenuItem value="daily">Journalier</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Taux de TVA (%)"
              type="number"
              value={formData.tax_rate}
              onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })}
              fullWidth
              inputProps={{ min: 0, max: 100, step: 0.01 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.allows_quantity}
                  onChange={(e) => setFormData({ ...formData, allows_quantity: e.target.checked })}
                />
              }
              label="Permettre la sélection de quantité"
            />
            {formData.allows_quantity && (
              <>
                <TextField
                  label="Quantité minimum"
                  type="number"
                  value={formData.min_quantity}
                  onChange={(e) => setFormData({ ...formData, min_quantity: parseInt(e.target.value) || 1 })}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
                <TextField
                  label="Quantité maximum"
                  type="number"
                  value={formData.max_quantity}
                  onChange={(e) => setFormData({ ...formData, max_quantity: parseInt(e.target.value) || 1 })}
                  fullWidth
                  inputProps={{ min: formData.min_quantity }}
                />
              </>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.track_stock}
                  onChange={(e) => setFormData({
                    ...formData,
                    track_stock: e.target.checked,
                    stock_quantity: e.target.checked ? (formData.stock_quantity ?? 0) : null,
                  })}
                />
              }
              label="Suivi du stock (article quantifiable)"
            />
            {formData.track_stock && (
              <TextField
                label="Quantité en stock"
                type="number"
                value={formData.stock_quantity ?? ''}
                onChange={(e) => setFormData({
                  ...formData,
                  stock_quantity: e.target.value === '' ? null : Math.max(0, parseInt(e.target.value, 10) || 0),
                })}
                fullWidth
                inputProps={{ min: 0 }}
                helperText="Ne pas vendre si stock à 0. Laisser vide = non quantifiable (ex. service)."
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingPrestation ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Prestations;

